import {
    ChangeDetectionStrategy,
    Component,
    computed,
    OnDestroy,
    OnInit,
    Signal,
    signal,
    WritableSignal,
} from "@angular/core";
import { Subject, takeUntil } from "rxjs";
import { RouterService } from "../../../../../shared/_services/router.service";
import { TradingSymbolsListsService } from "../../_services/trading-symbols-lists.service";
import { ActivatedRoute } from "@angular/router";
import { TradingSymbolView } from "../../../../../shared/_types/trading-symbol.type";
import { TradingSymbolsList, TradingSymbolsListType } from "../../../../../shared/_types/trading-symbols-list.type";
import { ListTradingSymbolsResponse, TradingSymbolsService } from "../../trading-symbols/trading-symbols.service";
import { CommandResult } from "../../../../../shared/_types/command-result.type";
import { NotificationsService } from "../../../../../shared/_services/notifications.service";

type SelectableTradingSymbol = TradingSymbolView & { isSelected: Signal<boolean> };

@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    selector: "app-edit-trading-symbols-lists",
    templateUrl: "./edit-trading-symbols-list.component.html",
    styleUrl: "./edit-trading-symbols-list.component.scss",
    standalone: false,
})
export class EditTradingSymbolsListComponent implements OnInit, OnDestroy {
    private ngUnsubscribe = new Subject<any>();
    public isNew: WritableSignal<boolean> = signal<boolean>(true);

    public isListLoaded: WritableSignal<boolean> = signal<boolean>(false);
    public areSymbolsLoaded: WritableSignal<boolean> = signal<boolean>(false);
    public tradingSymbols: WritableSignal<SelectableTradingSymbol[]> = signal<SelectableTradingSymbol[]>([]);
    public listedTradingSymbols: Signal<SelectableTradingSymbol[]> = computed(() =>
        this.tradingSymbols().filter(
            (symbol) =>
                (!this.showOnlySelected() || symbol.isSelected()) &&
                (this.filterText().length === 0 || symbol.name.includes(this.filterText())),
        ),
    );
    public showOnlySelected: WritableSignal<boolean> = signal<boolean>(false);
    public filterText: WritableSignal<string> = signal<string>("");
    public isLoaded: Signal<boolean> = computed(() => this.isListLoaded() && this.areSymbolsLoaded());

    public tradingSymbolsListId: WritableSignal<string> = signal<string>("");
    public tradingSymbolsListName: WritableSignal<string> = signal<string>("");
    public tradingSymbolsListDescription: WritableSignal<string> = signal<string>("");
    public tradingSymbolsListType: WritableSignal<TradingSymbolsListType> = signal<TradingSymbolsListType>(
        TradingSymbolsListType.StaticList,
    );
    public tradingSymbolsListSymbols: WritableSignal<string[]> = signal<string[]>([]);
    public file: WritableSignal<File | null> = signal<File | null>(null);

    public displayedColumns: string[] = ["isSelected", "symbol", "company", "group", "exchange", "type", "isActive"];

    public isValid: Signal<boolean> = computed(
        () =>
            this.tradingSymbolsListName() !== "" &&
            this.tradingSymbolsListType() !== TradingSymbolsListType.Unknown &&
            ((this.tradingSymbolsListType() === TradingSymbolsListType.StaticList &&
                this.tradingSymbolsListSymbols().length > 0) ||
                this.tradingSymbolsListType() === TradingSymbolsListType.StocksScreener),
    );

    constructor(
        private router: RouterService,
        private tradingSymbolsListsService: TradingSymbolsListsService,
        private tradingSymbolsService: TradingSymbolsService,
        private route: ActivatedRoute,
        private notificationsService: NotificationsService,
    ) {}

    ngOnInit(): void {
        this.tradingSymbolsService
            .list({ page: 1, itemsPerPage: 10000, searchText: "" })
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((symbolsResp: ListTradingSymbolsResponse) => {
                this.tradingSymbols.set(
                    symbolsResp.symbols.map((symbol) => ({
                        ...symbol,
                        isSelected: computed(() => this.tradingSymbolsListSymbols().includes(symbol.name)),
                    })),
                );
                this.areSymbolsLoaded.set(true);
            });

        const id = this.route.snapshot.paramMap.get("id");
        if (id === "new") {
            this.showOnlySelected.set(false);
            this.isListLoaded.set(true);
            return;
        }

        this.tradingSymbolsListsService
            .get(id!)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((list: TradingSymbolsList) => {
                this.tradingSymbolsListId.set(list.id);
                this.tradingSymbolsListName.set(list.name);
                this.tradingSymbolsListDescription.set(list.description);
                this.tradingSymbolsListType.set(list.type);
                this.tradingSymbolsListSymbols.set(list.symbols ?? []);

                this.showOnlySelected.set(true);
                this.isNew.set(false);
                this.isListLoaded.set(true);
            });
    }

    private tradingSymbolsList(): TradingSymbolsList {
        return {
            id: this.tradingSymbolsListId(),
            name: this.tradingSymbolsListName(),
            description: this.tradingSymbolsListDescription(),
            type: this.tradingSymbolsListType(),
            symbols: this.tradingSymbolsListSymbols(),
            screenerRules: null,
            isSystem: true,
        };
    }

    create(): void {
        this.tradingSymbolsListsService
            .create(this.tradingSymbolsList())
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((res: CommandResult) => {
                if (!res.success) {
                    this.notificationsService.showError("Error", res.error ?? "Failed to create trading symbols list");

                    return;
                }
                this.router.navigate("/trading-symbols-lists");
            });
    }

    update(): void {
        this.tradingSymbolsListsService
            .update(this.tradingSymbolsListId(), this.tradingSymbolsList())
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((res: CommandResult) => {
                if (!res.success) {
                    this.notificationsService.showError("Error", res.error ?? "Failed to update trading symbols list");

                    return;
                }
                this.router.navigate("/trading-symbols-lists");
            });
    }

    cancel(): void {
        this.router.navigate("/trading-symbols-lists");
    }

    ngOnDestroy(): void {
        // prevent memory leak when component destroyed
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }

    changeSelection(isSelected: boolean, symbolName: string): void {
        const currentSymbols = this.tradingSymbolsListSymbols();
        if (isSelected) {
            if (!currentSymbols.includes(symbolName)) {
                this.tradingSymbolsListSymbols.set([...currentSymbols, symbolName]);
            }
        } else {
            this.tradingSymbolsListSymbols.set(currentSymbols.filter((name) => name !== symbolName));
        }
    }

    onFileSelected(event: any): void {
        this.file.set(event.target.files[0]);
    }

    upload(): void {
        if (!this.file()) {
            return;
        }

        this.tradingSymbolsListsService
            .updateFromFile(this.tradingSymbolsListId(), this.file()!)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe({
                next: (res: CommandResult) => {
                    if (!res.success) {
                        this.notificationsService.showError("Error", res.error ?? "Failed to upload file");
                        return;
                    }
                    this.notificationsService.showSuccess("Success", "File uploaded successfully");
                    this.router.navigate("/trading-symbols-lists");
                },
                error: () => {
                    this.notificationsService.showError("Error", "Failed to upload file");
                },
            });
    }
}
